// =============================================================================
// MeuDriver — Tour de onboarding guiado (I3)
// Implementação via overlay CSS sem biblioteca externa.
// =============================================================================

import { useOnboarding } from '@/hooks/useOnboarding'
import { cn } from '@/lib/utils'
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

// ---------------------------------------------------------------------------
// Definição dos passos (aluno)
// ---------------------------------------------------------------------------

interface TourStep {
  title: string
  description: string
  position: 'center' | 'top' | 'bottom-left' | 'bottom-right'
}

const STEPS: TourStep[] = [
  {
    title: 'Bem-vindo ao MeuDriver!',
    description: 'Aqui voce encontra os melhores instrutores de direção particulares perto de você. Vamos te mostrar como aproveitar ao maximo a plataforma.',
    position: 'center',
  },
  {
    title: 'Encontre seu instrutor',
    description: 'Use a barra de busca e os filtros para encontrar instrutores por cidade, especialidade, avaliação e disponibilidade.',
    position: 'center',
  },
  {
    title: 'Acompanhe suas aulas',
    description: 'Na seção "Aulas" você vê todas as suas aulas agendadas, em andamento e concluídas. Chat direto com o instrutor incluído.',
    position: 'center',
  },
  {
    title: 'Ganhe pontos e conquistas',
    description: 'A cada aula concluída você ganha XP, pontos de fidelidade e desbloqueia conquistas. Progresso rumo à CNH gamificado!',
    position: 'center',
  },
  {
    title: 'Pronto para começar!',
    description: 'Você está pronto para agendar sua primeira aula. Encontre um instrutor agora e dê o primeiro passo rumo à sua CNH.',
    position: 'center',
  },
]

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

interface OnboardingTourProps {
  className?: string
}

export function OnboardingTour({ className }: OnboardingTourProps) {
  const { showTour, step, nextStep, prevStep, skipTour, totalSteps } = useOnboarding()

  if (!showTour) return null

  const currentStep = STEPS[step] ?? STEPS[0]
  const isLast = step === totalSteps - 1

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[1px]"
        onClick={skipTour}
        aria-hidden="true"
      />

      {/* Card flutuante centralizado */}
      <div
        className={cn(
          'fixed z-[9999] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[90vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100',
          'flex flex-col gap-5 p-6',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={`Tour: passo ${step + 1} de ${totalSteps}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-tour"
      >
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* Indicador de passo */}
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold shrink-0">
              {step + 1}
            </span>
            <span className="text-xs text-muted-foreground">de {totalSteps}</span>
          </div>
          <button
            type="button"
            onClick={skipTour}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Pular tour"
            data-testid="onboarding-skip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Barra de progresso */}
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-brand-600 transition-all duration-300"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Conteúdo */}
        <div className="space-y-2">
          <h2 className="text-lg font-bold text-gray-900">{currentStep.title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{currentStep.description}</p>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-2">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={prevStep}
              className="gap-1"
              data-testid="onboarding-prev"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Anterior
            </Button>
          )}

          <div className="flex-1" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={skipTour}
            className="text-muted-foreground text-xs"
          >
            Pular
          </Button>

          <Button
            type="button"
            size="sm"
            onClick={nextStep}
            className="gap-1 bg-brand-600 hover:bg-brand-700"
            data-testid="onboarding-next"
          >
            {isLast ? (
              <>
                <CheckCircle className="h-3.5 w-3.5" />
                Concluir
              </>
            ) : (
              <>
                Próximo
                <ChevronRight className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  )
}
